import api, { API_URL } from './api';

export type ManualCompetition = {
  id: number;
  club_id: string;
  club_type: string;
  code: string;
  name: string;
  external_id: string;
  matches_link?: string;
  table_link?: string;
  team_count?: string;
};

export type MatchesImportResult = {
  imported: number;
  updated: number;
  errors: string[];
};

export type TablesImportResult = {
  imported: number;
  errors: string[];
};

export type ManualCompetitionInput = {
  code: string;
  name: string;
  external_id: string;
  team_count?: string;
};

export async function listManualCompetitions(): Promise<ManualCompetition[]> {
  const res = await api.get<ManualCompetition[] | { data: ManualCompetition[] }>(
    '/admin/manual/competitions'
  );
  return Array.isArray(res.data) ? res.data : (res.data as any).data;
}

export async function createManualCompetition(payload: ManualCompetitionInput): Promise<ManualCompetition> {
  const res = await api.post<ManualCompetition | { data: ManualCompetition }>(
    '/admin/manual/competitions',
    payload
  );
  return (res.data as any).id ? (res.data as any) : (res.data as any).data;
}

export async function updateManualCompetition(id: number, payload: ManualCompetitionInput): Promise<ManualCompetition> {
  const res = await api.put<ManualCompetition | { data: ManualCompetition }>(
    `/admin/manual/competitions/${id}`,
    payload
  );
  return (res.data as any).id ? (res.data as any) : (res.data as any).data;
}

export async function deleteManualCompetition(id: number): Promise<void> {
  await api.delete(`/admin/manual/competitions/${id}`);
}

export function getMatchesTemplateUrl(): string {
  const base = (API_URL || '').replace(/\/$/, '');
  return `${base}/admin/manual/matches/template`;
}

export function getTablesTemplateUrl(): string {
  const base = (API_URL || '').replace(/\/$/, '');
  return `${base}/admin/manual/tables/template`;
}

/**
 * Import matches from a CSV or XLSX file.
 * @param file The file to import.
 * @returns The import result.
 */
export async function importManualMatches(file: File): Promise<MatchesImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<MatchesImportResult | { imported: number; updated: number; errors: string[] }>(
    '/admin/manual/matches/import',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  const data: any = res.data || {};
  return {
    imported: typeof data.imported === 'number' ? data.imported : 0,
    updated: typeof data.updated === 'number' ? data.updated : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
  };
}

/**
 * Import tables from a CSV or XLSX file.
 * @param file The file to import.
 * @returns The import result.
 */
export async function importManualTables(file: File): Promise<TablesImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<TablesImportResult | { imported: number; errors: string[] }>(
    '/admin/manual/tables/import',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  const data: any = res.data || {};
  return {
    imported: typeof data.imported === 'number' ? data.imported : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
  };
}

/**
 * Import matches from a JSON payload.
 * @param payload The JSON payload to import.
 * @returns The import result.
 */
export async function importManualMatchesJSON(payload: any): Promise<MatchesImportResult> {
  const res = await api.post<MatchesImportResult | { imported: number; updated: number; errors: string[] }>(
    '/admin/manual/matches/import-json',
    payload,
  );
  const data: any = res.data || {};
  return {
    imported: typeof data.imported === 'number' ? data.imported : 0,
    updated: typeof data.updated === 'number' ? data.updated : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
  };
}

/**
 * Import tables from a JSON payload.
 * @param payload The JSON payload to import.
 * @returns The import result.
 */
export async function importManualTablesJSON(payload: any): Promise<TablesImportResult> {
  const res = await api.post<TablesImportResult | { imported: number; errors: string[] }>(
    '/admin/manual/tables/import-json',
    payload,
  );
  const data: any = res.data || {};
  return {
    imported: typeof data.imported === 'number' ? data.imported : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
  };
}
